import React from "react";
import PropTypes from "prop-types";
import { filterSafeText, filterNameText } from "../../utils/validation/safeText";
import { GreenTextField } from "./greenTextField";

const AUTO_EXEMPT_TYPES = new Set(["password", "email", "number"]);
const PASSWORD_FIELD_NAMES = new Set([
  "password",
  "confirmPassword",
  "currentPassword",
  "newPassword",
  "oldPassword",
]);

/**
 * GreenTextField that strips disallowed characters on change (LDCP-228).
 * Pass sanitize={false} for URL, IP, JSON, PEM, GraphQL query, WAF match values, etc.
 * Single-line sanitized fields collapse repeated spaces by default (name-style inputs).
 */
export function SafeTextField({
  sanitize = true,
  collapseSpaces,
  multiline,
  type,
  name,
  onChange,
  ...props
}) {
  const isPasswordField = type === "password" || PASSWORD_FIELD_NAMES.has(name);
  const shouldSanitize = sanitize && !AUTO_EXEMPT_TYPES.has(type) && !isPasswordField;
  const shouldCollapseSpaces = collapseSpaces ?? (!multiline && shouldSanitize);

  const handleChange = (event) => {
    if (!onChange) return;
    if (!shouldSanitize) {
      onChange(event);
      return;
    }
    const raw = event.target.value;
    const filtered = shouldCollapseSpaces ? filterNameText(raw) : filterSafeText(raw);
    if (filtered === raw) {
      onChange(event);
      return;
    }
    onChange({ ...event, target: { ...event.target, value: filtered } });
  };

  return (
    <GreenTextField
      type={type}
      name={name}
      multiline={multiline}
      onChange={handleChange}
      {...props}
    />
  );
}

SafeTextField.propTypes = {
  sanitize: PropTypes.bool,
  collapseSpaces: PropTypes.bool,
  multiline: PropTypes.bool,
  type: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
};

/** SafeTextField with repeated-space collapse always enabled. */
export function NameSafeTextField(props) {
  return <SafeTextField collapseSpaces {...props} />;
}

export default SafeTextField;
